import { Dispatch } from "redux";
import { ConsultingIncome, ConsultingIncomeUI, IConsultingIncomeQuery } from "models/consulting-income";
import { setReport } from "./reducer";
import { toast } from "react-toastify";
import { buildQueryString } from "utils/query-string";
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar";
import calcPercent from "utils/calc-percent";
import Branch from "models/branch";


export const downloadConsultingIncomeCSV = (report: ConsultingIncome[], query: IConsultingIncomeQuery) => {
    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)
    const toLabel = (code: number) => calendar.fiscalToLabel(Math.floor(code / 100), code % 100)
    const metadata = `From: ${toLabel(query.fromFiscalPeriod ?? calendar.todayFiscalPeriod())} | To: ${toLabel(query.toFiscalPeriod ?? calendar.todayFiscalPeriod())}`

    const headers = [
        "Department", "Actual", "Budget",
        "Variance ($)", "Variance (%)",
        "WIF Forecast",
        "Variance Actual VS Forecast ($)", "Variance Actual VS Forecast (%)",
    ]

    const toRow = (row: ConsultingIncome) => {
        const budgetVariance = row.actual - row.budget
        const budgetVariancePct = calcPercent(row.actual, row.budget)
        const wihVariance = row.actual - row.workInHand
        const wihVariancePct = calcPercent(row.actual, row.workInHand)
        return [
            row.department,
            ConsultingIncomeUI.format(row.actual),
            ConsultingIncomeUI.format(row.budget),
            ConsultingIncomeUI.format(budgetVariance),
            ConsultingIncomeUI.format(budgetVariancePct) + "%",
            ConsultingIncomeUI.format(row.workInHand),
            ConsultingIncomeUI.format(wihVariance),
            ConsultingIncomeUI.format(wihVariancePct) + "%",
        ]
    }

    const totals = report.reduce(
        (acc, row) => ({
            department: "Total",
            actual: acc.actual + row.actual,
            budget: acc.budget + row.budget,
            workInHand: acc.workInHand + row.workInHand,
        }),
        { department: "Total", actual: 0, budget: 0, workInHand: 0 }
    )

    const rows = report.map(toRow)

    const csv = [[metadata], [], headers, ...rows, toRow(totals)]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `consulting-income-${query.fromFiscalPeriod}-${query.toFiscalPeriod}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

export const getConsultingIncomeSearchFields = async (): Promise<{ branches: Branch[] } | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/consulting-income/search-fields`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting consulting income search fields");
        return null;
    }
}

export const getAllConsultingIncome =(query: IConsultingIncomeQuery) => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/consulting-income${buildQueryString(query)}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        dispatch(setReport(data.data));
    } catch (err: any) {
        toast.error(err.message ?? "Error getting consulting income");
    }
}
